import CryptoJS from 'crypto-js';
 
// Secret key for encryption - In production, use environment variables
const SECRET_KEY = process.env.ENCRYPTION_KEY || 'Secret-Key';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  TOKEN_EXP,
  USER_DATA,
  ID_TOKEN,
} from '../../constants/applicationConstants';
import { http } from 'msw';
 
/**
 * Sets a cookie with secure options
 * @param {string} name - The name of the cookie
 * @param {string} value - The value of the cookie
 * @param {number} expiresIn - Expiration time in seconds (optional)
 * @param {object} options - Additional cookie options
 * @returns {void}
 */
export const setCookie = (name, value, expiresIn = null, options = {}) => {
  try {
    let cookieString = `${name}=${encodeURIComponent(value)}`;
 
    if (expiresIn) {
      const expirationDate = new Date();
      expirationDate.setSeconds(expirationDate.getSeconds() + expiresIn);
      cookieString += `; expires=${expirationDate.toUTCString()}`;
    }
 
    // Default secure options
    const defaultOptions = {
      path: '/',
      sameSite: 'Lax',
      ...options,
    };
 
    // Add SameSite attribute
    cookieString += `; SameSite=${defaultOptions.sameSite}`;
    // Add Path attribute
    cookieString += `; Path=${defaultOptions.path}`;
 
    // Only set Secure flag in production (https)
    if (window.location.protocol === 'https:') {
      cookieString += '; Secure';
    }
    document.cookie = cookieString;
  } catch (error) {
    console.error('Error setting cookie:', error);
  }
};
 
/**
 * Gets a cookie value by name
 * @param {string} name - The name of the cookie
 * @returns {string|null} - The cookie value or null if not found
 */
export const getCookie = (name) => {
  try {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
 
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting cookie:', error);
    return null;
  }
};
 
/**
 * Removes a cookie by name
 * @param {string} name - The name of the cookie to remove
 * @returns {void}
 */
export const removeCookie = (name) => {
  try {
    setCookie(name, '', -1);
  } catch (error) {
    console.error('Error removing cookie:', error);
  }
};
 
/**
 * Removes all cookies
 * @returns {void}
 */
export const removeAllCookies = () => {
  try {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      removeCookie(name);
    }
  } catch (error) {
    console.error('Error removing all cookies:', error);
  }
};
 
/**
 * Encrypts data and stores it in a cookie
 * @param {string} name - The cookie name
 * @param {any} data - The data to encrypt and store (will be JSON stringified if not string)
 * @param {number} expiresIn - Expiration time in seconds (optional)
 * @param {object} options - Additional cookie options
 * @returns {boolean} - Returns true if successful, false if failed
 */
export const encryptAndStoreCookie = (name, data, expiresIn = null, options = {}) => {
  try {
    // Convert data to JSON string if not already a string
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
    // Encrypt the data
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
    // Set the cookie
    setCookie(name, encrypted, expiresIn, options);
    return true;
  } catch (error) {
    console.error('Error encrypting and storing cookie:', error);
    return false;
  }
};
 
/**
 * Retrieves data from a cookie and decrypts it
 * @param {string} name - The cookie name
 * @returns {any|null} - Returns decrypted data or null if failed/not found
 */
export const retrieveAndDecryptCookie = (name) => {
  try {
    const encryptedData = getCookie(name);
    if (!encryptedData) {
      return null; // Cookie doesn't exist
    }
 
    // Decrypt the data
    const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
 
    if (!decryptedString) {
      console.warn(`Failed to decrypt cookie: ${name}`);
      return null;
    }
 
    // Try to parse as JSON, if it fails return as string
    try {
      return JSON.parse(decryptedString);
    } catch (parseError) {
      // If it's not JSON, return as string
      return decryptedString;
    }
  } catch (error) {
    console.error('Error retrieving and decrypting cookie:', error);
    return null;
  }
};
 
/**
 * Checks if a cookie exists
 * @param {string} name - The cookie name
 * @returns {boolean} - Returns true if cookie exists
 */
export const hasCookie = (name) => {
  return getCookie(name) !== null;
};
 
/**
 * Gets all cookies as an object
 * @returns {object} - Object with cookie names as keys and values as values
 */
export const getAllCookies = () => {
  try {
    const cookies = {};
    const cookieList = document.cookie.split(';');
 
    for (let i = 0; i < cookieList.length; i++) {
      const cookie = cookieList[i].trim();
      const eqPos = cookie.indexOf('=');
      if (eqPos > 0) {
        const name = cookie.substring(0, eqPos);
        const value = decodeURIComponent(cookie.substring(eqPos + 1));
        cookies[name] = value;
      }
    }
    return cookies;
  } catch (error) {
    console.error('Error getting all cookies:', error);
    return {};
  }
};
 
/**
 * Sets multiple authentication cookies with guaranteed completion
 * @param {object} authTokens - Object containing token data
 * @param {string} authTokens.accessToken - Access token
 * @param {string} authTokens.idToken - ID token
 * @param {string} authTokens.refreshToken - Refresh token
 * @param {string} authTokens.expiresIn - Token expiration time in seconds
 * @param {object} authTokens.userData - User data object for encryption
 * @returns {Promise<boolean>} - Returns true when all cookies are set
 */
export const setAuthTokensCookie = async (authTokens) => {
  return new Promise((resolve) => {
    try {
      const { accessToken, idToken, refreshToken, expiresIn, userData } = authTokens;
 
      // Set all auth tokens as cookies using configured keys
      if (accessToken) {
        setCookie(ACCESS_TOKEN, accessToken);
      }
      if (idToken) {
        setCookie(ID_TOKEN, idToken);
      }
      if (refreshToken) {
        setCookie(REFRESH_TOKEN, refreshToken);
      }
 
      // Normalise TOKEN_EXP to an absolute Unix timestamp (seconds since epoch)
      // so that AuthContext.checkTokens and scheduleTokenRefresh can treat it
      // consistently as an expiry moment.
      if (expiresIn) {
        const nowSeconds = Math.floor(Date.now() / 1000);
        const lifetime = Number(expiresIn) || 0;
        const absoluteExp = nowSeconds + lifetime;
        setCookie(TOKEN_EXP, String(absoluteExp));
      }
 
      // Encrypt and store user data under configured key
      if (userData) {
        encryptAndStoreCookie(USER_DATA, userData);
      }
 
      // Use requestIdleCallback if available, otherwise use setTimeout to yield
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => resolve(true));
      } else {
        setTimeout(() => resolve(true), 0);
      }
    } catch (error) {
      console.error('Error setting auth tokens cookies:', error);
      resolve(false);
    }
  });
};
 
 